import { gr } from "src/design-system/tokens";

// The one feed post look (community feed, dashboard, post popup): text, full-width media, the
// hairlines around the action bar and the comment thread. The author line (UserInfoCard), counts row
// (ActionsCount) and action bar (HandleAction) style themselves.
// Full-width media bleeds into the side padding: the container sets `--feed-post-pad` to its padding.
export const feedPostSx = {
  fontFamily: gr.font.family,
  // Post text: 14 ink, line breaks kept; an empty text takes no space.
  "& .post-description": {
    marginTop: `${gr.space[3]} !important`,
    marginBottom: 0,
  },
  "& .post-description:has(> p:empty)": { display: "none" },
  "& .post-description > p": {
    display: "block",
    margin: 0,
    fontSize: gr.font.size.md,
    fontWeight: gr.font.weight.regular,
    color: gr.color.ink,
    lineHeight: gr.font.lineHeight.normal,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },
  "& .feed-media": {
    marginTop: gr.space[3],
    marginLeft: "calc(-1 * var(--feed-post-pad))",
    marginRight: "calc(-1 * var(--feed-post-pad))",
  },
  "& .feed-media:empty": { display: "none" },
  // Video: full width, 16:9, letterboxed on ink.
  "& .feed-media .wrapper": {
    position: "relative",
    paddingTop: 0,
    aspectRatio: "16 / 9",
    background: gr.color.ink,
  },
  "& .feed-media .player": { position: "absolute", inset: 0 },
  "& .feed-counts": { marginTop: gr.space[3] },
  // Hairlines above and below the action bar.
  "& > hr": {
    margin: `${gr.space[1]} 0`,
    border: 0,
    borderTop: `1px solid ${gr.color.line}`,
    opacity: 1,
  },
  "& > hr:first-of-type": { marginTop: gr.space[3] },
  // Second hairline only when something follows it (comments / comment box).
  "& > hr:has(+ .profile-comments:empty)": { display: "none" },
  // With no comments the action bar is the last thing: less space under it, so the Like / Comment
  // text sits as far from the line above as from the card's edge below.
  "&:has(> .profile-comments:empty)": { paddingBottom: gr.space[2] },
  // Comments: avatar + tinted bubble — name · time on one line, the text, Like · Reply and the
  // like count; a quiet ⋮ inside the bubble; replies indented under the parent's text.
  "& .single-comment-card": {
    background: gr.color.tint,
    borderRadius: gr.radius.tile,
    padding: gr.space[3],
    paddingRight: gr.space[10],
    gap: gr.space[3],
    fontFamily: gr.font.family,
  },
  "& .single-comment-card .ps-2": { paddingLeft: "0 !important" },
  "& .single-comment-card .MuiAvatar-root": {
    width: gr.control.sm,
    height: gr.control.sm,
    fontSize: `calc(${gr.control.sm} * 0.4)`,
    flex: "none",
  },
  "& .single-comment-card .poster-name": {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    columnGap: gr.space[2],
  },
  "& .single-comment-card .poster-name .card-title": {
    fontSize: gr.font.size.md,
    fontWeight: gr.font.weight.semibold,
    color: gr.color.ink,
  },
  "& .single-comment-card .poster-name .date-color, & .single-comment-card > .w-100 > .date-color":
    {
      float: "none !important",
      margin: "0 !important",
      padding: "0 !important",
      fontSize: `${gr.font.size.xs} !important`,
      fontWeight: `${gr.font.weight.regular} !important`,
      color: `${gr.color.muted} !important`,
      opacity: 1,
    },
  "& .single-comment-card > .w-100 > .date-color": { display: "block" },
  "& .single-comment-card .post-description": {
    margin: `${gr.space[1]} 0 0 !important`,
  },
  "& .single-comment-card .post-description > span.date-color": {
    display: "block",
    fontSize: `${gr.font.size.md} !important`,
    fontWeight: `${gr.font.weight.regular} !important`,
    lineHeight: gr.font.lineHeight.normal,
    color: `${gr.color.ink2} !important`,
    opacity: 1,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  "& .single-comment-card .post-description > img": {
    display: "block",
    maxWidth: "min(240px, 100%)",
    maxHeight: 240,
    marginTop: gr.space[2],
    borderRadius: gr.radius.chip,
    objectFit: "cover",
    cursor: "zoom-in",
  },
  // The row carries the legacy .date-color class (50% opacity): keep Like · Reply fully readable.
  "& .single-comment-card .like-comment": {
    marginTop: `${gr.space[2]} !important`,
    alignItems: "center",
    opacity: 1,
  },
  "& .single-comment-card .like-reply-box": {
    display: "inline-flex",
    alignItems: "center",
  },
  "& .single-comment-card .comment-action": {
    fontFamily: gr.font.family,
    fontSize: gr.font.size.sm,
    fontWeight: gr.font.weight.medium,
    color: gr.color.brandDark,
    borderRadius: gr.radius.chip,
    "&:hover": { textDecoration: "underline", textUnderlineOffset: "3px" },
    "&.Mui-focusVisible": { boxShadow: gr.shadow.focus },
  },
  "& .single-comment-card .comment-action.liked-comment": {
    color: `${gr.color.danger} !important`,
  },
  "& .single-comment-card .comment-action + .comment-action::before": {
    content: '"·"',
    display: "inline-block",
    margin: `0 ${gr.space[2]}`,
    color: gr.color.muted,
  },
  "& .single-comment-card .comment-like-heart": {
    display: "inline-flex",
    alignItems: "center",
    gap: gr.space[1],
    fontFamily: gr.font.family,
    fontSize: gr.font.size.sm,
    color: gr.color.ink2,
    borderRadius: gr.radius.chip,
    "& svg": { color: gr.color.danger },
    "&.Mui-focusVisible": { boxShadow: gr.shadow.focus },
  },
  // ⋮ menu: small and quiet in the bubble's top-right corner.
  "& .single-comment-card-dots": {
    position: "absolute",
    top: gr.space[2],
    right: gr.space[2],
  },
  // Replies: under the parent's text (avatar + gap), with a thread line.
  "& .profile-comments .col-1": { display: "none" },
  "& .profile-comments .col-11.ms-auto": {
    width: "auto",
    marginLeft: `calc(${gr.control.sm} + ${gr.space[3]}) !important`,
    paddingLeft: gr.space[3],
    borderLeft: `2px solid ${gr.color.line}`,
  },
  "& .view-comment": { marginTop: gr.space[3] },
};
